import type { GetContextMenuItemsParams, GetMainMenuItemsParams, MenuItemDef } from "ag-grid-community";
import { getProcessedSelectedCellsByRange } from "../../../tableUtils";
import type { TColumnHeaderProps } from "../components";
import { artisTypeSchema } from "../keyboard/utils";
import { isMobile } from "../../../shared/hooks";
import { getCellIds } from "../cell-style/helpers";
import type { useStore } from "jotai";
import type { useFormulaViewerModal } from "../modals/FormulaViewer";
import type { useConfigureAlertsModal } from "../modals/Alerts";
import type { useColumnSettingsModal } from "../modals/Formatting";
import type { useInsertCurveDuplicate } from "../../menu-system/configs/sections/curve";
import type { useClearFormatting } from "../../menu-system/configs/sections/format";
import type { TQuickAccess } from "../keyboard/useQuickAccess";
import type {
  useUpdatePageProducts,
  useInsertPageProducts,
  useQueryConditionalFormattingRules,
  useUpsertConditionalFormattingRules,
  useConditionalFormattingCache,
  useDeletePageProducts,
  usePage,
  useUpdatePage,
  useAllProducts,
  useGlobalProducts,
} from "../../../data";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";
import type { useConditionalFormattingModal } from "../modals/ConditionalFormatting";
import type { useRemoveColumnDialog } from "../../menu-system/configs/sections/remove-column";
import type { useTraceTracePrecedentsModal } from "../../menu-system/configs/sections/trace-precedents";
import type { useCurveSettingsModal } from "../../components/CurveSettingsModal";
import type { GridApi } from "ag-grid-community";
import type { useCurrentUser, useUserSubscriptionTier } from "../../../data/user";

export type GetContextMenuSection = (p: ReturnType<typeof getMenuItemContext>) => MenuItemDef[];

/**
 * Dynamic dependencies injected into ActionContext.
 * These are provided by hooks at the top level and passed down.
 */
type DynamicContextData = {
  // Auth & User (used by: all sections for permission checks)
  user: ReturnType<typeof useCurrentUser>;
  disabledFeatures: ReturnType<typeof useUserSubscriptionTier>["disabledFeatures"];
  /** From org admin "Needs Historical Data" — used by live chart restriction (WEB-1790). */
  orgNeedsHistoricalData: boolean;
  /** Feature flag "live-chart-feed-selection" — gates feed-aware "View Live Chart" logic. */
  liveChartFeedSelectionEnabled: boolean;
  /** Keyed map of all products (by id) — used by feed-aware live chart logic to read the right-clicked curve. */
  productsById: ReturnType<typeof useAllProducts>["data"];
  /** Global products (third-party feeds) with per-user permissions — used by feed-aware live chart logic. */
  globalProducts: ReturnType<typeof useGlobalProducts>["data"];

  // Jotai Store (used by: curve section for grid settings)
  store: ReturnType<typeof useStore>;

  // Modals (used by: curve section, show-formula section, alerts section, period section, range section, conditional-formatting section, remove-column section, trace-precedents section)
  formulaModal: ReturnType<typeof useFormulaViewerModal>;
  insertCurveModal: { open: (props: { curveWidth?: number }) => void; close: () => void; visible: boolean };
  alertsModal: ReturnType<typeof useConfigureAlertsModal>;
  columnSettingsModal: ReturnType<typeof useColumnSettingsModal>;
  conditionalFormattingModal: ReturnType<typeof useConditionalFormattingModal>;
  openRemoveColumnDialog: ReturnType<typeof useRemoveColumnDialog>;
  tracePrecedentsModal: ReturnType<typeof useTraceTracePrecedentsModal>;
  curveSettingsModal?: ReturnType<typeof useCurveSettingsModal>;

  // Mutations (used by: curve section for duplicate, add-to-favourites section, column section, conditional-formatting section, remove-column section)
  insertCurveDuplicate: ReturnType<typeof useInsertCurveDuplicate>;
  upsertQuickAccess: { mutateAsync: (qa: TQuickAccess) => Promise<unknown> };
  updatePageProducts: ReturnType<typeof useUpdatePageProducts>;
  insertPageProducts: ReturnType<typeof useInsertPageProducts>;
  deletePageProducts: ReturnType<typeof useDeletePageProducts>;
  upsertConditionalFormattingRules: ReturnType<typeof useUpsertConditionalFormattingRules>;

  // Formatting actions (used by: period section, range section, column section)
  clearFormatting: ReturnType<typeof useClearFormatting>;

  // Conditional formatting data (used by: conditional-formatting section)
  conditionalFormattingRules: ReturnType<typeof useQueryConditionalFormattingRules>["data"];
  conditionalFormattingCache: ReturnType<typeof useConditionalFormattingCache>;

  // Grid API (used by: conditional-formatting section)
  gridApi: GridApi | null;

  // Page products data (used by: column section, insert-column section)
  pageProductsData: PageProductsFragmentFragment[] | undefined;

  // Quick Access data (used by: add-to-favourites section and useQuickAccess hook)
  quickAccessData: (TQuickAccess & { productName: string })[];

  // Grid settings (used by: remove-column section, insert-column section)
  dontShowRemoveCurveWarning: boolean;
  insertColumnPosition: import("../../../data").InsertPosition;
  defaultCurveColumnWidth: number;

  // Page ID (used by: add-to-favourites section, period section, range section, column section)
  pageId: string;

  // User ID (used by: add-to-favourites section, column section)
  userId: string;

  // Page data and mutations (used by: header section)
  page: ReturnType<typeof usePage>["data"];
  updatePage: ReturnType<typeof useUpdatePage>;

  // Globalised-folder edit gating (WEB-1953 / WEB-2028).
  // Product "admin" = umi-internal-admin / umi-internal-write.
  isUmiInternalAdmin: boolean;
  globalisedPackageIds: ReadonlySet<number>;
};

/**
 * Builds the ActionContext from AG Grid params and dynamic dependencies.
 * This context is available to all menu item actions, disabled checks, and tooltips.
 */
export const getMenuItemContext = (
  params: GetContextMenuItemsParams | GetMainMenuItemsParams,
  dynamicData: DynamicContextData,
) => {
  const {
    user,
    disabledFeatures,
    orgNeedsHistoricalData,
    liveChartFeedSelectionEnabled,
    productsById,
    globalProducts,
    store,
    formulaModal,
    insertCurveModal,
    alertsModal,
    columnSettingsModal,
    conditionalFormattingModal,
    openRemoveColumnDialog,
    tracePrecedentsModal,
    curveSettingsModal,
    insertCurveDuplicate,
    upsertQuickAccess,
    updatePageProducts,
    insertPageProducts,
    deletePageProducts,
    upsertConditionalFormattingRules,
    clearFormatting,
    conditionalFormattingRules,
    conditionalFormattingCache,
    gridApi,
    pageProductsData,
    quickAccessData,
    dontShowRemoveCurveWarning,
    insertColumnPosition,
    defaultCurveColumnWidth,
    pageId,
    userId,
    page,
    updatePage,
    isUmiInternalAdmin,
    globalisedPackageIds,
  } = dynamicData;
  const api = params.api;
  const isHeaderMenu = !("node" in params) || params.node === undefined;
  const headerParams = params.column?.getColDef().headerComponentParams;
  const columnWidth = params.column?.getActualWidth();
  const artisType = artisTypeSchema.safeParse(headerParams?.artisType).data;
  const exchange = headerParams?.exchange;
  const maturity = headerParams?.maturity;
  const productId = headerParams?.productId;
  const columnId = headerParams?.gridId || "";
  const entityId = headerParams?.entityId || "";
  const rowId = (params as GetContextMenuItemsParams)?.node?.id;
  const headerName = params.column?.getColDef().headerName;
  const isProduct = !!productId;
  const isHeaderMenuItem = isHeaderMenu || isMobile;
  const cannedOrSourced = ["canned", "sourced"].includes(artisType || "");
  const isShadowCurve = Boolean(columnId?.includes("shadow") || headerParams?.shadowType || (columnId && !isProduct));
  const hasFormula = headerParams?.hasFormula ?? false;
  const selectedRange = getProcessedSelectedCellsByRange({ api });
  const rangeSelection = selectedRange[0] || [];
  const isBlank = headerParams?.shadowType === "blank";
  const periodCount = getCellIds({ type: "period", ranges: selectedRange, api }).cellIds.length;
  const columnCount = getCellIds({ type: "column", ranges: selectedRange, api }).cellIds.length;

  return {
    // Core AG Grid
    api,
    isHeaderMenu,

    // Column metadata
    columnId,
    columnWidth,
    productId,
    entityId,
    exchange,
    maturity,
    artisType,
    headerName,
    headerParams: headerParams as TColumnHeaderProps | undefined,
    rowId,

    // Derived grid state
    isProduct,
    isHeaderMenuItem,
    isShadowCurve,
    cannedOrSourced,
    hasFormula,
    isBlank,
    isMobile,

    // Selection & range
    selectedRange,
    rangeSelection,
    periodCount,
    columnCount,

    // Auth & user
    user,
    disabledFeatures,
    orgNeedsHistoricalData,
    userId,

    // Feed-aware live chart
    liveChartFeedSelectionEnabled,
    productsById,
    globalProducts,

    // Action dependencies
    store,
    formulaModal,
    insertCurveModal,
    alertsModal,
    columnSettingsModal,
    conditionalFormattingModal,
    openRemoveColumnDialog,
    tracePrecedentsModal,
    curveSettingsModal,
    insertCurveDuplicate,
    upsertQuickAccess,
    updatePageProducts,
    insertPageProducts,
    deletePageProducts,
    upsertConditionalFormattingRules,
    clearFormatting,
    conditionalFormattingRules,
    conditionalFormattingCache,
    gridApi,
    pageProductsData,
    quickAccessData,
    dontShowRemoveCurveWarning,
    insertColumnPosition,
    defaultCurveColumnWidth,
    pageId,
    page,
    updatePage,

    // Globalised-folder gating
    isUmiInternalAdmin,
    globalisedPackageIds,
  };
};
